---
title: "QR-код vCard для визитки: что добавить и как проверить перед печатью"
description: "Как создать статический QR-код контакта для визитки: поля vCard, приватность, PNG или SVG и чек-лист тестирования тиража."
locale: "ru"
date: "2026-07-23"
tags: ["qr", "vcard", "визитка", "контакты"]
relatedTools: ["qr-generator"]
translations:
  en: "vcard-qr-code-for-business-card"
faq:
  - q: "Можно ли изменить контакт в QR-коде после печати визитки?"
    a: "Нет. В статическом vCard QR данные записаны прямо в матрицу. При смене телефона, email или сайта нужно создать новый код и перепечатать визитку."
  - q: "Какие данные можно добавить в vCard QR на qrka?"
    a: "Имя, организацию, телефон, email и сайт. Лучше включать только те рабочие контакты, которые вы готовы показывать каждому владельцу визитки."
  - q: "Есть ли у статического vCard QR статистика сканирований?"
    a: "Нет. Контактные данные записаны прямо в код, а qrka не ведёт статистику его сканирований."
  - q: "Контакт отправляется на сервер при генерации?"
    a: "Нет. Контакт обрабатывается в браузере и не отправляется на сервер. Готовый QR можно скачать в PNG или SVG."
---

На визитке мало места, а человеку нужно сохранить имя, телефон, email и сайт
без ручного набора. vCard QR записывает эти поля в код: после сканирования
совместимый телефон предлагает создать контакт.

Откройте [генератор QR-кодов](/qr-kod?type=vcard), выберите vCard и заполните только нужные
поля, скачайте SVG или PNG и проверьте код в реальном размере до заказа
тиража. Генератор создаёт статический QR локально в браузере.

## Какие поля включать

Qrka поддерживает имя, организацию, телефон, email и сайт. Для рабочей визитки
этого обычно достаточно.

| Поле | Когда добавлять | Что проверить |
| --- | --- | --- |
| Имя | Почти всегда | Как оно должно выглядеть в адресной книге |
| Организация | Для рабочего контакта | Актуальное название, без лишнего слогана |
| Телефон | Если вы принимаете звонки | Код страны и отсутствие опечаток |
| Email | Для письменной связи | Рабочий адрес и правильный домен |
| Сайт | Для портфолио или компании | Полный действующий URL |

Не превращайте vCard в резюме. Чем больше данных записано в матрицу, тем
плотнее получается код и тем труднее его сканировать в маленьком размере.
Должность, мессенджеры, длинное описание и десяток ссылок лучше вынести на
страницу сайта, а в статическом контакте оставить минимум.

## Контакт в QR не является секретом

Статический vCard QR хранит поля прямо в рисунке. Любой человек, у которого
есть визитка или её фотография, может прочитать имя, телефон, email,
организацию и сайт. QR-код кодирует данные, но не шифрует их.

Используйте рабочие контакты, предназначенные для распространения. Домашний
адрес, личный номер, внутреннюю почту и другие закрытые данные в печатный код
добавлять не стоит. Для Wi-Fi действует похожее правило, но там нужен отдельный
сценарий с [гостевой сетью](/blog/qr-kod-dlya-wifi-gostevaya-set), а не vCard.

Контактные поля обрабатываются в браузере и не отправляются на сервер. Но после
печати вы сознательно публикуете их каждому, кто получит или сфотографирует
визитку.

## Статический или динамический вариант

Статический vCard подходит, если контакты стабильны и вам не нужна статистика
сканирований. Для него не нужны учётная запись и подписка. Компромисс в том,
что данные нельзя изменить после печати, а узнать число сканирований не
получится.

Ограничение такое же прямое: после печати нельзя заменить телефон, email или
сайт. Если данные изменились, нужен новый QR и новый тираж. Если вы ожидаете
частые изменения или хотите считать сканы, сначала сравните
[статический и динамический QR](/blog/staticheskij-qr-kod-ili-dinamicheskij).
Qrka создаёт только статические коды: изменить их содержимое после печати или
посмотреть статистику сканирований нельзя.

## PNG или SVG для макета

SVG удобнее для типографии и векторного макета: его можно масштабировать без
размытия. PNG подходит, если редактор или печатный сервис принимает только
растровые изображения; в этом случае экспортируйте код с достаточным размером
и не растягивайте маленький PNG после вставки.

Независимо от формата:

- оставьте свободное поле вокруг матрицы;
- используйте тёмные модули на светлом фоне;
- не обрезайте края и не накрывайте код текстом или логотипом;
- не размещайте его на сгибе, перфорации или слишком бликующем участке;
- проверяйте уже свёрстанную визитку, а не только картинку из генератора.

Qrka блокирует сочетания цветов с контрастом ниже 4.5:1, но хороший показатель
в редакторе не заменяет пробную печать. Бумага, ламинация, размер и качество
печати тоже влияют на считывание.

## Чек-лист перед тиражом

1. Отсканируйте код минимум на двух разных телефонах.
2. Проверьте, какие поля предлагает сохранить приложение контактов.
3. Позвоните по номеру и откройте email и сайт из сохранённой карточки.
4. Распечатайте один экземпляр в финальном размере и повторите сканирование.
5. Проверьте код при обычном офисном свете, а не только с экрана.
6. Сохраните исходные контактные данные рядом с макетом, чтобы заметить
   изменения перед следующим тиражом.

Отправляйте в печать только тот макет, который прошёл проверку в физическом
размере. Для статического QR это последний момент, когда опечатку можно
исправить без перепечатки.
